import { FundOverviewService } from '@products/services/fund-overview.service';
import { Injectable, OnDestroy } from '@angular/core';
import {
  NAV,
  PremiumDiscountAnalysisPeriod,
  PremiumDiscountElevated,
  PricingHistory,
  Product,
  SalesChargeBreakPoint,
} from '@models';
import { Logger } from '@utils/logger';
import {
  dateRangeToggleInitialValues,
  premiumDiscountDateRangeToggleInitialValues,
  PRICING_INITIAL_STATE,
} from './pricing-distribution.config';
import { PricingHistoryService } from '@products/services/pricing-history.service';
import {
  FundId,
  HighLowNavData,
  PricingState,
  PricingDownloadableTableRowData,
  ShareClassCode,
  PricingConfig,
} from '@types';
import { SiteConfigService } from '@services/site-config.service';
import { ConfigurationId, ProductDetailConfiguration } from '@types';
import { symbols } from '@utils/il8n/currency';
import { toLocaleNumber } from '@utils/il8n/number-il8n';
import { getHighchartsLocaleFormat } from '@utils/text/date-utils';
import { forkJoin, Observable, Subject } from 'rxjs';
import pricingHistoricalData from '@graphql/pricing-distribution/pricing-historical.graphql';
import premiumDiscountData from '@graphql/pricing-distribution/premium-discount.graphql';

import pricingNavData from '@graphql/pricing-distribution/pricing.graphql';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { SeriesOptionsType } from 'highcharts';
import find from 'lodash/find';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import cloneDeep from 'lodash/cloneDeep';
import { TranslateService } from '@shared/translate/translate.service';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { PremiumDiscountService } from '@products/services/premium-discount.service';
import { hasValue, hasValues } from '@products/utils/mappers/mapper.utils';
import { EMDASH } from '@products/utils/constants/product.constants';
import { parseNumber } from '@utils/text/number-utils';
import { PremiumDiscountElevatedPeriod } from '../premium-discount-analysis/premium-discount-elevated/premium-discount-elevated.component';
import { DebugService } from '@services/debug.service';
import { InfoItem, PagedRecords } from '@frk/eds-components';
dayjs.extend(isBetween);

const logger = Logger.getLogger('PricingService');

interface NavChartLabels {
  navLabel: string;
  navChangePercentLabel: string;
  marketPriceLabel: string;
  marketPriceChangePercentLabel: string;
  viivLabel: string;
}

enum CalcType {
  'NAV',
  'MARKET_PRICE',
  'PREMIUM_DISCOUNT',
  'PREMIUM_DISCOUNT_NAV_VIIV',
}

type CustomSeriesOptions = SeriesOptionsType & {
  min: number;
  currencySymbol: string;
};

const HIGHEST_LOWEST_NAV_DISABLED_COUNTRIES = new Set(['BR', 'CA']);

@Injectable({
  providedIn: 'root',
})
export class PricingService implements OnDestroy {
  private state: PricingState;
  private configurationName: ConfigurationId;
  private _pricingState: Subject<PricingState>;
  protected pagedRecords: PagedRecords<PricingHistory>;
  isSiteIntl: boolean;
  isSiteUcits: boolean;
  isTemit: boolean;
  isMartinCurrieGpt: boolean;
  private currentFundId: FundId;
  private currentShareClassCode: ShareClassCode;
  private productTypesForPOP: string[] = [
    ConfigurationId.MF,
    ConfigurationId.TIF,
    ConfigurationId._529,
    ConfigurationId.IF,
    ConfigurationId.TOF,
  ];

  private productTypesForMarketPrice: string[] = [
    ConfigurationId.ETF,
    ConfigurationId.CEF,
  ];

  private productTypesForPremiumDiscounts: string[] = [
    ConfigurationId.ETF,
    ConfigurationId.CEF,
  ];

  private unsubscribe$: Subject<void> = new Subject<void>();

  private siteConfigDateFormat: string;

  /**
   * default pagesize for the tables
   */
  pageSize = 10;

  constructor(
    private pricingHistoryService: PricingHistoryService,
    private siteConfigService: SiteConfigService,
    private fundOverviewService: FundOverviewService,
    private premiumDiscountService: PremiumDiscountService,
    private translateService: TranslateService,
    private caveatService: CaveatService,
    private debugService: DebugService
  ) {
    this.pagedRecords = new PagedRecords<PricingHistory>(this.pageSize); // Page size set to '10', ref. by prod site.
    this.isSiteIntl = this.siteConfigService.isSiteInternational(); // Set the site international flag value accordingly.
    this.isSiteUcits = this.siteConfigService.isSiteUcits(); // Set the site UCITS(Non US and Canada)
    this.isTemit = this.siteConfigService.isTemit();
    this.isMartinCurrieGpt = this.siteConfigService.isMartinCurrieGpt();
    this._pricingState = new Subject();
    this.siteConfigDateFormat = this.siteConfigService
      .getGlobalConfig()
      ?.siteConfiguration?.site?.dateFormat?.toUpperCase();
  }

  public get pricingState$(): Subject<PricingState> {
    return this._pricingState;
  }

  /**
   * Call register method of fund overview & pricing history service.
   * @param productDetailConfiguration the configuration received on page detail load
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    // only want to call data services if for different fund or share class
    if (
      this.currentFundId !== productDetailConfiguration.fundId ||
      this.currentShareClassCode !== productDetailConfiguration.shareClassCode
    ) {
      this.currentFundId = productDetailConfiguration.fundId;
      this.currentShareClassCode = productDetailConfiguration.shareClassCode;
      forkJoin([
        this.fundOverviewService.register(pricingNavData, {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }),
        this.pricingHistoryService.register(pricingHistoricalData, {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }),
      ]).subscribe((mergedData: [Product, PricingHistory[]]) => {
        logger.debug(mergedData);
        this.mapState(mergedData);
      });
    }
  }

  /**
   * Split the merged details and map the overview & pricing data to the state accordingly.
   * Map the decorated config and any other setting to the state.
   * @param mergedData merged response for product & pricing history.
   */
  mapState(mergedData: [Product, PricingHistory[]]): void {
    this.state = cloneDeep(PRICING_INITIAL_STATE);

    const [overviewData, pricingHistories] = mergedData;
    const isHideNavPercentChange = this.siteConfigService.hidePercentChangeNavOnChart();

    this.state.overviewData.shareClass = overviewData.shareClasses[0];
    this.state.overviewData.nav = overviewData.shareClasses[0]?.nav;
    this.state.overviewData.salesChargeBreakPoints = this.getSalesChargeBreakPoints(
      overviewData
    );
    this.state.overviewData.fundName = overviewData.fundName;
    this.state.historicalData = pricingHistories;

    this.state.pricingConfig.productType = overviewData.productType;
    this.state.pricingConfig.showApplicationPrice =
      this.siteConfigService.showApplicationPrice() &&
      overviewData.productType !== ConfigurationId.ETF;
    this.state.pricingConfig.showRedemptionPrice =
      this.siteConfigService.showRedemptionPrice() &&
      overviewData.productType !== ConfigurationId.ETF;
    this.state.pricingConfig.showForETF =
      overviewData.productType === ConfigurationId.ETF;
    this.state.pricingConfig.showPop =
      !this.isSiteIntl &&
      this.productTypesForPOP.includes(overviewData.productType) &&
      !this.siteConfigService.hidePopHistoricalPricingTable(
        this.currentShareClassCode,
        overviewData.productType
      );
    this.state.pricingConfig.showMarketPriceData =
      (!this.isSiteUcits &&
        this.productTypesForMarketPrice.includes(overviewData.productType) &&
        this.siteConfigService.isTraded(this.currentFundId)) ||
      this.isTemit ||
      this.isMartinCurrieGpt;
    this.state.pricingConfig.showPremiumDiscountData = this.showPremiumDiscountDataPricingConfig(
      overviewData
    );
    this.state.pricingConfig.showMoneyMarketPriceData =
      !this.isSiteIntl && overviewData.productType === ConfigurationId.MMF;
    this.state.pricingConfig.showMoneyMarketNetFlowColumn =
      this.state.pricingConfig.showMoneyMarketPriceData &&
      pricingHistories.some((pricingHistory) =>
        hasValue(pricingHistory, 'netShareHolderFlow')
      );
    this.state.pricingConfig.showMoneyMarketMasterNetFlowColumn =
      this.state.pricingConfig.showMoneyMarketPriceData &&
      pricingHistories.some((pricingHistory) =>
        hasValue(pricingHistory, 'masterNetShareHolderFlow')
      );

    this.state.pricingConfig.isSecondaryCalcTypeAvailable = this.isSecondaryCalcTypeAvailable(
      pricingHistories
    );
    // DCE-1947, Fetched hidePercentChangeNavOnChart from segment level site configuration
    this.state.pricingConfig.suppressNavChangePercentage = isHideNavPercentChange; // To-Do: make this from state accordingly.
    this.state.pricingConfig.dateValues.incDate =
      pricingHistories[pricingHistories.length - 1]?.asOfDate; // Property used for excel data formatting
    this.state.pricingConfig.dateValues.incDateStd =
      pricingHistories[pricingHistories.length - 1]?.asOfDateStd;
    this.state.pricingConfig.dateValues.asOfDateStd =
      pricingHistories[0]?.asOfDateStd;
    this.state.isSiteIntl = this.isSiteIntl;
    this.state.isTemit = this.isTemit;
    this.state.isMartinCurrieGpt = this.isMartinCurrieGpt;
    this.state.siteConfig = this.siteConfigService.product?.site;
    this.checkVIPData();
    this.state.chartConfig.highChartOptions = this.fetchChartOptions();
    this.createPriceChart(this.state.chartConfig.selectedToggleRange);
    this.createPremiumDiscountNavViivChart(
      this.state.chartConfig.selectedToggleRange
    ); // set to ONE_MONTH initially

    this.state.highLowNavTableData = this.getHighLowNavTableData(
      this.state.overviewData.nav
    );

    this.createPriceTable(this.state.tableConfig.selectedToggleRange);

    this.createPremiumDiscountChart('ONE_MONTH'); // set to ONE_MONTH initially
    logger.debug('premiumDiscountHistory', this.state.premiumDiscountHistory);

    this.state.premiumDiscountElevated = overviewData.shareClasses[0]?.premiumDiscountElevated?.map(
      (period: PremiumDiscountElevated): PremiumDiscountElevatedPeriod => {
        const reasonKey = `products.${period.eventType}-${period.periodId}`;
        return {
          endDate: period.endDate,
          tradingDays: period.tradingDays,
          reason: this.translateService.instant(reasonKey),
          reasonTooltip: this.translateService.tooltip(reasonKey),
        };
      }
    );
    logger.debug(
      'premiumDiscountElevated, ',
      this.state.premiumDiscountElevated,
      overviewData.shareClasses[0]
    );
    this.state.pricingConfig.hideIntraDayNav =
      overviewData.productType === ConfigurationId.MMF &&
      !this.siteConfigService.isSiteInternational();

    this.state.pricingConfig.hidePricingChart = this.siteConfigService.hideHistoricalPricingChart(
      this.currentFundId
    );

    // NGC-9089 don't show Premium Discount Chart for Closed End Funds
    // Show Premium Discount Chart only in case of ETF
    if (overviewData.productType === ConfigurationId.ETF) {
      this.premiumDiscountService
        .register(premiumDiscountData, {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: this.currentFundId,
          shareClassCode: this.currentShareClassCode,
        })
        .subscribe((premiumDiscount: PremiumDiscountAnalysisPeriod[]) => {
          this.state.premiumDiscountAnalysis = {
            fundId: overviewData.fundId,
            // filter out any periods that don't have data
            periods: premiumDiscount.filter(
              (period: PremiumDiscountAnalysisPeriod): boolean => !!period
            ),
          };
          this.updateStates();
        });
    } else {
      this.updateStates();
    }
  }
  /**
   *
   * this method helps in checking if values exist in response then 'masterFundNav', 'dailyLiquidAssetRatio',
   * 'weeklyLiquidAssetRatio' and 'netShareHolderFlow' these values should be visibile in table and excel
   */
  private checkVIPData(): void {
    if (
      !this.isSiteIntl &&
      this.state.pricingConfig.productType === ConfigurationId.VIP
    ) {
      this.state.pricingConfig.showMasterFundNav = this.state.historicalData.some(
        (row) => row.masterFundNav !== EMDASH && row.masterFundNav
      );
      this.state.pricingConfig.showDailyLiquidAssetRatio = this.state.historicalData.some(
        (row) =>
          row.dailyLiquidAssetRatio !== EMDASH && row.dailyLiquidAssetRatio
      );
      this.state.pricingConfig.showWeeklyLiquidAssetRatio = this.state.historicalData.some(
        (row) =>
          row.weeklyLiquidAssetRatio !== EMDASH && row.weeklyLiquidAssetRatio
      );
      this.state.pricingConfig.showNetShareHolderFlow = this.state.historicalData.some(
        (row) => row.netShareHolderFlow !== EMDASH && row.netShareHolderFlow
      );
    }
  }
  private showPremiumDiscountDataPricingConfig(oData: Product): boolean {
    // NGC-13964: Show Premium / Discount for Canada ETF to match US version
    if (this.siteConfigService.isCanada()) {
      return oData.productType === 'ETFs';
    }
    return (
      (!this.isSiteIntl &&
        this.productTypesForPremiumDiscounts.includes(oData.productType) &&
        this.siteConfigService.isTraded(this.currentFundId)) ||
      this.isTemit ||
      this.isMartinCurrieGpt
    );
  }

  updateStates() {
    this.state.hasLoadedFullFeed = true;
    this._pricingState.next(this.state);
    // remove current fundId and share class code flags to ensure we repopulate if we come back to the same fund
    this.currentFundId = null;
    this.currentShareClassCode = null;
  }

  public createPremiumDiscountChart(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): void {
    if (!hasValues(this.state.historicalData, 'discountOrPremiumAtLastClose')) {
      // no Premium Discount data, so return null
      this.state.premiumDiscountHistory = null;
      return;
    }
    logger.debug('createPremiumDiscountChart() HAS VALUES!');
    const {
      filteredPricingData,
      fromDateStd,
      toDateStd,
    } = this.getFilteredPricingDataByDateRange(
      this.state.historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    const chartConfig = {
      chartTitle: this.translateService.instant(
        'products.premium-discount-historical-header'
      ),
      proximalPlacementTop: 'PremiumDiscountHistoryChartProximalTop',
      proximalPlacementBottom: 'PremiumDiscountHistoryChartProximalBottom',
      caveatPlacement: 'PremiumDiscountHistoryChart',
      tooltip: this.translateService.tooltip(
        'products.premium-discount-historical-header'
      ),
      dateRangeEnum: premiumDiscountDateRangeToggleInitialValues(),
      selectedToggleRange,
      customRange: {
        fromDateStd,
        toDateStd,
      },
      highChartOptions: this.fetchChartOptions('PREMIUM_DISCOUNT'),
    };
    chartConfig.highChartOptions.series = this.fetchPremiumDiscountSeries(
      filteredPricingData
    );
    logger.debug('Premium Discount chartConfig', chartConfig);

    this.state.premiumDiscountHistory = chartConfig;

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this._pricingState.next(this.state);
    }
  }

  /**
   * Fetch chart series data as per pricing history.
   * @param navHistory pricing history available
   * @param currencyCode currency code used in pricing
   * @param navChartLabels chart labels for calctypes.
   * @param suppressNavChangePercentage boolean value to show nav change percentage or not
   */
  fetchPremiumDiscountSeries(
    navHistory: PricingHistory[]
  ): SeriesOptionsType[] {
    const navValues = this.convertNavHistoryIntoSeries(
      navHistory,
      CalcType.PREMIUM_DISCOUNT
    );

    const premiumDiscountSeries: SeriesOptionsType = {
      name: this.translateService.instant(
        'products.premium-discount-market-price-vs-nav'
      ),
      type: 'line',
      tooltip: {
        pointFormat: `
          <span style='font-size:14px;color:{series.color}'>\u25CF</span>
          <span>{series.name}: </span>
          <b>{point.y}%</b>
        `,
      },
      data: navValues,
    };

    return [premiumDiscountSeries];
  }

  private getSalesChargeBreakPoints(
    overviewData: Product
  ): SalesChargeBreakPoint[] {
    return overviewData.shareClasses[0]?.salesChargeBreakPoints
      ? overviewData.shareClasses[0].salesChargeBreakPoints
          .filter(
            (salesChargeBreakPoint) =>
              salesChargeBreakPoint.dataCategory ===
              'POPandSalesChargeBreakPoints'
          )
          .sort((salesChargeBreakPoint, otherSalesChargeBreakPoint) => {
            return (
              salesChargeBreakPoint.breakPointTypeKey -
              otherSalesChargeBreakPoint.breakPointTypeKey
            );
          })
      : [];
  }

  setConfigurationName(configurationName: ConfigurationId): void {
    this.configurationName = configurationName;
  }

  getConfigurationName(): ConfigurationId {
    return this.configurationName;
  }

  /**
   * ******************** Pricing chart helper methods ********************
   */

  /**
   * Return boolean value, if market price data is available.
   * @param priceHistories nav price history data
   */
  isSecondaryCalcTypeAvailable(priceHistories: PricingHistory[]): boolean {
    return priceHistories.some((pricingHistory: PricingHistory) =>
      hasValue(pricingHistory, 'marketPriceValue')
    );
  }

  /**
   * Fetch initial chart options.
   */
  fetchChartOptions(
    chartType: 'NAV' | 'PREMIUM_DISCOUNT_NAV_VIIV' | 'PREMIUM_DISCOUNT' = 'NAV'
  ): Highcharts.Options {
    const currentInstance = this;
    return {
      title: {
        text: undefined,
      },
      chart: {
        spacingLeft: 5,
        spacingRight: 0,
        marginRight: 20,
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      xAxis: {
        type: 'datetime',
        gridLineDashStyle: 'LongDash',
        // COREWEB-2921 increase tickPixelInterval from default 100, to force extra width for labels
        // NB: new value found by trial and error, and doesn't seem to reflect the actual pixel width
        tickPixelInterval: 110,
        // WORKAROUND: COREWEB-1543 set hour/minute/second to day display
        dateTimeLabelFormats: {
          millisecond: '%e. %b',
          second: '%e. %b',
          minute: '%e. %b',
          hour: '%e. %b',
        },
      },
      yAxis: [
        {
          gridLineDashStyle: 'LongDash',
          labels: {
            format:
              chartType === 'PREMIUM_DISCOUNT'
                ? '{value}%'
                : this.getCurrencySymbol(
                    this.state.overviewData.nav.currencyCode
                  ) + '{value}',
          },
          title: {
            text: null,
          },
          minorTickInterval: 'auto',
        },
      ],
      legend: {
        enabled: true,
        verticalAlign: 'bottom',
        useHTML: true,
        labelFormatter() {
          const label = `${currentInstance.translateService.instant(
            'products.toggle'
          )} ${this.name} ${currentInstance.translateService.instant(
            'products.visibility'
          )}`;
          return `<span role="button" aria-pressed="true" aria-label="${label}">${this.name}</span>`;
        },
      },
      accessibility: {
        enabled: true, // Enable accessibility options for the legend
        keyboardNavigation: {
          enabled: true,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        // COREWEB-1277 set to day precision
        // http://api.highcharts.com/highcharts/tooltip.dateTimeLabelFormats
        xDateFormat: getHighchartsLocaleFormat(this.siteConfigDateFormat),
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
        },
      },
    };
  }

  /**
   * Filter the historical price chart data as per the toggle range selected.
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  createPriceChart(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): void {
    const {
      filteredPricingData,
      fromDateStd,
      toDateStd,
    } = this.getFilteredPricingDataByDateRange(
      this.state.historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    this.state.chartConfig.chartTitle = this.translateService.instant(
      'products.price-history-chart-header'
    );
    this.state.chartConfig.proximalPlacementTop =
      'HistoricalPriceChartProximalTop';
    this.state.chartConfig.proximalPlacementBottom =
      'HistoricalPriceChartProximalBottom';
    this.state.chartConfig.caveatPlacement = 'HistoricalPriceChart';
    this.state.chartConfig.tooltip = this.translateService.tooltip(
      'products.price-history-chart-header'
    );

    const navChartLabels = this.getNavChartLabels();
    this.state.chartConfig.highChartOptions.series = this.fetchChartSeries(
      filteredPricingData,
      this.state.overviewData.nav.currencyCode,
      navChartLabels,
      this.state.pricingConfig.suppressNavChangePercentage
    );

    // Also, set the date values in state.
    this.state.chartConfig.selectedToggleRange = selectedToggleRange;
    this.state.chartConfig.customRange.fromDateStd = fromDateStd;
    this.state.chartConfig.customRange.toDateStd = toDateStd;

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this._pricingState.next(this.state);
    }
  }

  /**
   * Filter the historical feed as per the date range, if available, and return accordingly.
   * @param historicalData historical data from feed
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  getFilteredPricingDataByDateRange(
    historicalData: PricingHistory[],
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): {
    filteredPricingData: PricingHistory[];
    fromDateStd: string;
    toDateStd: string;
  } {
    let filteredPricingData: PricingHistory[];
    let fromDateStd: string;
    let toDateStd: string;

    if (selectedToggleRange === 'CUSTOM_RANGE' && toDate) {
      toDateStd = dayjs(toDate).format('YYYY-MM-DD');
    } else if (selectedToggleRange === 'LAST_QUARTER') {
      toDateStd = dayjs()
        .subtract(1, 'quarter')
        .endOf('quarter')
        .format('YYYY-MM-DD');
    } else if (selectedToggleRange === 'LAST_CALENDAR_YEAR') {
      toDateStd = dayjs()
        .subtract(1, 'year')
        .endOf('year')
        .format('YYYY-MM-DD');
    } else {
      // Assigned toDateStd to be as of pricing history's first nav date.
      // This will compensate the possibility of last nav date less than today's date.
      toDateStd = dayjs(historicalData[0]?.asOfDateStd).format('YYYY-MM-DD');
    }

    if (selectedToggleRange === 'CUSTOM_RANGE' && fromDate) {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        dayjs(fromDate).format('YYYY-MM-DD'),
        toDateStd
      );
    } else {
      fromDateStd = this.calculateFromDateStd(
        selectedToggleRange,
        this.state.pricingConfig.dateValues.incDateStd,
        toDateStd
      );
    }

    // LOGIC: Filter the pricing history (original data) from state, as per 'from/to' standard date, by dayjs library.
    filteredPricingData = historicalData.filter(
      (pricingHistory: PricingHistory) =>
        dayjs(pricingHistory.asOfDateStd, 'YYYY-MM-DD').isBetween(
          fromDateStd,
          toDateStd,
          null,
          '[]'
        )
    );

    return {
      filteredPricingData,
      fromDateStd,
      toDateStd,
    };
  }

  /**
   * Calculate from date value in standard format.
   * @param type the key of the DATE_RANGE_ENUM map
   * @param startDateStd start date standard
   * @param endDateStd end date standard
   */
  calculateFromDateStd(
    type: string,
    startDateStd: string,
    endDateStd: string
  ): string {
    const startMoment = dayjs(startDateStd, 'YYYY-MM-DD');
    const endMoment = dayjs(endDateStd, 'YYYY-MM-DD');
    let startOfYear: Dayjs;
    let startOfQtr: Dayjs;

    switch (type) {
      case 'CUSTOM_RANGE':
        // custom range could be anything up to the full inception range
        return startDateStd;
      case 'SINCE_INCEPTION':
        return startDateStd;
      case 'YTD':
        startOfYear = endMoment.startOf('year');
        if (startOfYear.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfYear.format('YYYY-MM-DD');
      case 'LAST_QUARTER':
        startOfQtr = endMoment.startOf('quarter');
        if (startOfQtr.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfQtr.format('YYYY-MM-DD');
      case 'LAST_CALENDAR_YEAR':
        startOfYear = endMoment.startOf('year');
        if (startOfYear.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return startOfYear.format('YYYY-MM-DD');
      default:
        const period = this.state.chartConfig.dateRangeEnum.get(type).period;
        const fromDate = endMoment.subtract(
          period.value as number,
          period.key as dayjs.UnitType
        );
        if (fromDate.isBefore(startMoment)) {
          return startMoment.format('YYYY-MM-DD');
        }
        return fromDate.format('YYYY-MM-DD');
    }
  }

  /**
   * Return the chart label-name (nav/market-price) as an object.
   */
  getNavChartLabels(): NavChartLabels {
    return {
      navLabel: this.translateService.instant(
        'products.price-history-chart-nav'
      ),
      navChangePercentLabel: this.translateService.instant(
        'products.price-history-chart-nav-change-percent'
      ),
      marketPriceLabel:
        this.state.isTemit || this.state.isMartinCurrieGpt
          ? this.translateService.instant(
              'products.price-history-chart-share-price'
            )
          : this.translateService.instant(
              'products.price-history-chart-market-price'
            ),
      marketPriceChangePercentLabel:
        this.state.isTemit || this.state.isMartinCurrieGpt
          ? this.translateService.instant(
              'products.price-history-chart-share-price-change-percent'
            )
          : this.translateService.instant(
              'products.price-history-chart-market-price-change-percent'
            ),
      viivLabel: this.translateService.instant(
        'products.price-history-chart-viiv'
      ),
    };
  }

  /**
   * Fetch chart series data as per pricing history.
   * @param navHistory pricing history available
   * @param currencyCode currency code used in pricing
   * @param navChartLabels chart labels for calctypes.
   * @param suppressNavChangePercentage boolean value to show nav change percentage or not
   */
  fetchChartSeries(
    navHistory: PricingHistory[],
    currencyCode: string,
    navChartLabels: NavChartLabels,
    suppressNavChangePercentage: boolean
  ): SeriesOptionsType[] {
    const navValues = this.convertNavHistoryIntoSeries(
      navHistory,
      CalcType.NAV
    );
    const currencySymbol = this.getCurrencySymbol(currencyCode);

    // COREWEB-6246 remove HTML from label
    // we cannot remove whole html, because sometimes label contains html tag
    // we need to remove only tooltip code, which is added automatically in getLabel
    // code is removing <a> tag with its content from label
    // first we need to remove new lines, which are quite often in labels
    // this can be refactored in the future and support for HTML in charts title may be easily added - extensive testing required
    const navLabel = navChartLabels.navLabel
      ?.replace(/(\r\n|\n|\r)/gm, '')
      .replace(/<a([^>]+?)([^>]*?)>(.*?)<\/a>/gi, '');

    const navSeries: CustomSeriesOptions = {
      name: navLabel,
      type: 'line',
      min: 0,
      tooltip: {
        pointFormatter: this.pointFormatterProvider(
          suppressNavChangePercentage,
          navChartLabels.navChangePercentLabel,
          false,
          this.state.pricingConfig.showMoneyMarketPriceData
        ),
      },
      data: navValues,
      currencySymbol, // this will be available as this.series.userOptions.currencySymbol in pointFormatterProvider()
    };

    let marketPriceSeries: CustomSeriesOptions = null;
    if (this.state.pricingConfig.isSecondaryCalcTypeAvailable) {
      const marketPriceValues = this.convertNavHistoryIntoSeries(
        navHistory,
        CalcType.MARKET_PRICE
      );

      marketPriceSeries = {
        name: navChartLabels.marketPriceLabel,
        type: 'line',
        min: 0,
        tooltip: {
          pointFormatter: this.pointFormatterProvider(
            suppressNavChangePercentage,
            navChartLabels.marketPriceChangePercentLabel,
            this.state.pricingConfig.isSecondaryCalcTypeAvailable
          ),
        },
        data: marketPriceValues,
        currencySymbol, // this will be available as this.series.userOptions.currencySymbol in pointFormatterProvider()
      };
    }

    return this.state.pricingConfig.isSecondaryCalcTypeAvailable &&
      this.state.pricingConfig.showMarketPriceData
      ? [navSeries, marketPriceSeries]
      : [navSeries];
  }

  /**
   * Return the currency code as special character against alphabet format.
   * @param currencyCode the currency code in three character string format (alphabet).
   */
  getCurrencySymbol(currencyCode: string): string {
    return symbols.get(currencyCode);
  }

  /**
   * Convert nav price histories into chart series.
   * @param navHistory nav price historic values
   * @param calcType NAV/MARKET_PRICE
   */
  convertNavHistoryIntoSeries(
    navHistory: PricingHistory[],
    calcType: CalcType
  ) {
    const navValues = [];

    if (navHistory) {
      const navHistoryLength = navHistory.length - 1;
      for (let c = navHistoryLength; c >= 0; c--) {
        const navPoint = navHistory[c];
        let ts = this.formatStdDateToUnixTimeStamp(navPoint.asOfDateStd);
        let val: number = null;

        // COREWEB-1539 : edge case 2017-05-05 00:00 being parsed as 2017-05-04 24:00, add a second
        // ps quicker than .set('second', 1)
        ts += 1 * 1000;
        switch (calcType) {
          case CalcType.NAV:
            val = parseFloat(navPoint.navStd);
            break;
          case CalcType.MARKET_PRICE:
            val = parseFloat(navPoint.marketPriceValueStd);
            break;
          case CalcType.PREMIUM_DISCOUNT:
            val = parseFloat(navPoint.discountOrPremiumAtLastCloseStd);
            break;
          case CalcType.PREMIUM_DISCOUNT_NAV_VIIV:
            val = parseNumber(navPoint.viiv);
            break;
        }
        // NGC-10109 0 is a valid value, but evaluates falsey
        if ((val || val === 0) && !isNaN(val)) {
          navValues.push([ts, val]);
        }
      }
    }

    return navValues;
  }

  /**
   * Highcharts tooltip render function.
   * @param suppressNavChangePercentage boolean value to show nav change percentage or not
   * @param changePercentLabel label name for secondary calc type
   * @param isSecondaryCalcTypeAvailable boolean for availability of secondary calc type
   */
  pointFormatterProvider(
    suppressNavChangePercentage: boolean,
    changePercentLabel: string,
    isSecondaryCalcTypeAvailable: boolean = false,
    showMoneyMarket: boolean = false
  ) {
    return function (): any {
      let baseLineNav = parseFloat(this.series.data[0].y);

      // COREWEB-4911 : first point can be invalid
      // WDE-1202 WARNING: baseLineNav can still be undefined after this in case where all NAV values are 0
      if (!baseLineNav) {
        const baseLineNavPoint = find(this.series.data, (dataPoint) => {
          return !!dataPoint.y;
        });
        if (baseLineNavPoint) {
          baseLineNav = parseFloat(baseLineNavPoint.y);
        }
      }

      const navValue = parseFloat(this.y);
      const navChangePercentSinceStart = (navValue / baseLineNav - 1) * 100;

      // FIXME: Remove hard-coded color coding scheme from here.
      let percentageColour = 'gray';
      if (navChangePercentSinceStart > 0) {
        percentageColour = '#4e9d2d';
      }
      if (navChangePercentSinceStart < 0) {
        percentageColour = '#cb0000';
      }
      const toolTipNavValue = showMoneyMarket ? navValue.toFixed(4) : navValue;
      let tooltipHtml = `
        <span style='font-size:14px;color:${this.series.color}'>\u25CF</span>
        <span>${this.series.name}: </span>
        <b>${this.series.userOptions.currencySymbol}${toolTipNavValue} </b>
      `;

      // COREWEB-1582 nav percentage in tooltip, added red for negative values
      // COREWEB-1583 nav percentage Pricing Chart Configuration : make available in Product Config
      // COREWEB-3902 in swiss locale currency is £3.00 and percentage is 4,00%, hence toLocaleNumber(xxx, 'percentage')
      if (!suppressNavChangePercentage) {
        // WDE-1202 use EMDASH when navChangePercentSinceStart is undefined
        const changeVal: string = isNaN(navChangePercentSinceStart)
          ? EMDASH
          : `${toLocaleNumber(navChangePercentSinceStart, 'percentage')}%`;
        tooltipHtml += `
          <br/>
          <span style='font-size:14px;color:rgba(0,0,0,0)'>\u25CF</span>
          <span>${changePercentLabel}: </span>
          <b style='color:${percentageColour}'>${changeVal}</b>
        `;
      }

      // COREWEB-3375 prefix with line break if Market Price
      if (isSecondaryCalcTypeAvailable) {
        tooltipHtml = '<br/>' + tooltipHtml;
      }

      return tooltipHtml;
    };
  }

  /**
   * Return UTC standard unix timestamp, when given std format day eg YYYY-MM-DD
   * @param val date in format locale or YYYY-MM-DD
   * @returns UTC unix timestamp
   */
  formatStdDateToUnixTimeStamp(val) {
    if (!val || val === '-') {
      return null;
    }

    const stdDateFormatMatch = val.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
    if (!stdDateFormatMatch) {
      return null;
    }

    const year = stdDateFormatMatch[1];
    const month = stdDateFormatMatch[2] - 1;
    const day = stdDateFormatMatch[3];
    const date = Date.UTC(year, month, day, 0, 0);

    return date.valueOf();
  }

  /**
   * ******************** High/Low Nav helper method ********************
   */

  /**
   * Create & return high low nav list object as per table format.
   */
  getHighLowNavTableData(navData: NAV): HighLowNavData[] {
    const highLowNavTableData: HighLowNavData[] = [];

    // calculate for ytd i.e., current year.
    if (
      navData.highestNavValueAsYearToDate &&
      navData.lowestNavValueAsYearToDate &&
      navData.highestNavValueDateAsYearToDate &&
      navData.lowestNavValueDateAsYearToDate
    ) {
      highLowNavTableData.push({
        highestNav: navData.highestNavValueAsYearToDate,
        lowestNav: navData.lowestNavValueAsYearToDate,
        highestNavDate: navData.highestNavValueDateAsYearToDate,
        lowestNavDate: navData.lowestNavValueDateAsYearToDate,
        year: new Date().getFullYear(),
      });
    }

    // calculate for 1 year to 9 years back.
    for (let index = 1; index < 10; index++) {
      if (
        navData[`highestNavValue${index}YearBack`] &&
        navData[`lowestNavValue${index}YearBack`] &&
        navData[`highestNavValueDate${index}YearBack`] &&
        navData[`lowestNavValueDate${index}YearBack`]
      ) {
        highLowNavTableData.push({
          highestNav: navData[`highestNavValue${index}YearBack`],
          lowestNav: navData[`lowestNavValue${index}YearBack`],
          highestNavDate: navData[`highestNavValueDate${index}YearBack`],
          lowestNavDate: navData[`lowestNavValueDate${index}YearBack`],
          year: new Date().getFullYear() - index,
        });
      }
    }

    return highLowNavTableData;
  }

  /**
   * ******************** Pricing table helper method ********************
   */

  /**
   * Filter the historical price table data as per the toggle range selected.
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  createPriceTable(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): void {
    const {
      filteredPricingData,
      fromDateStd,
      toDateStd,
    } = this.getFilteredPricingDataByDateRange(
      this.state.historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    // Also, set the date values in state.
    this.state.tableConfig.selectedToggleRange = selectedToggleRange;
    this.state.tableConfig.customRange.fromDateStd = fromDateStd;
    this.state.tableConfig.customRange.toDateStd = toDateStd;
    this.state.tableConfig.customRange.fromDate =
      filteredPricingData[filteredPricingData.length - 1]?.asOfDate;
    this.state.tableConfig.customRange.toDate =
      filteredPricingData[0]?.asOfDate;

    if (filteredPricingData.length > 0) {
      const maxNavPricingHistory: PricingHistory = maxBy(
        filteredPricingData,
        (pricingHistory: PricingHistory) => Number(pricingHistory.navStd)
      );
      const minNavPricingHistory: PricingHistory = minBy(
        filteredPricingData,
        (pricingHistory: PricingHistory) => Number(pricingHistory.navStd)
      );

      this.state.tableConfig.highestNav = maxNavPricingHistory.nav;
      this.state.tableConfig.lowestNav = minNavPricingHistory.nav;
      this.state.tableConfig.highestNavDate = maxNavPricingHistory.asOfDate;
      this.state.tableConfig.lowestNavDate = minNavPricingHistory.asOfDate;

      // WDE-1197 just check if FILTERED data has market price values
      if (this.isSecondaryCalcTypeAvailable(filteredPricingData)) {
        const maxMarketPricePricingHistory: PricingHistory = maxBy(
          filteredPricingData,
          (pricingHistory: PricingHistory) =>
            Number(pricingHistory.marketPriceValueStd)
        );
        const minMarketPricePricingHistory: PricingHistory = minBy(
          filteredPricingData.filter((pricingHistory: PricingHistory) => {
            return pricingHistory.marketPriceValueStd;
          }),
          (pricingHistory: PricingHistory) =>
            Number(pricingHistory.marketPriceValueStd)
        );

        this.state.tableConfig.highestMarketPrice =
          maxMarketPricePricingHistory.marketPriceValue;
        this.state.tableConfig.lowestMarketPrice =
          minMarketPricePricingHistory.marketPriceValue;
        this.state.tableConfig.highestMarketPriceDate =
          maxMarketPricePricingHistory.asOfDate;
        this.state.tableConfig.lowestMarketPriceDate =
          minMarketPricePricingHistory.asOfDate;
      } else {
        // if no data, make sure market price props are reset
        this.state.tableConfig.highestMarketPrice = null;
        this.state.tableConfig.lowestMarketPrice = null;
        this.state.tableConfig.highestMarketPriceDate = null;
        this.state.tableConfig.lowestMarketPriceDate = null;
      }
    }

    this.pagedRecords.records = filteredPricingData; // set current filtered data to pagination stuff.

    // expand tables for Smarsh capture
    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean) => {
        if (expandAll) {
          this.pageSize = this.pagedRecords.expandAll();
        }
      });

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this._pricingState.next(this.state);
    }
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   */
  triggerDownloadAction(): void {
    const mappedBody: PricingDownloadableTableRowData[] = this.mapDownloadData(
      this.getTotalFilteredRecords()
    );

    const mappedHeader = new Map<string, string>();

    if (this.state.pricingConfig.showApplicationPrice) {
      mappedHeader.set('asOfDate', 'products.date');
      mappedHeader.set('applicationPrice', 'pds.APPLICATION_PRICE');
      mappedHeader.set('redemptionPrice', 'pds.REDEMPTION_PRICE');
      mappedHeader.set('nav', 'pds.NAV_VALUE');
      mappedHeader.set('navChangeValue', 'pds.DAILY_NAV_CHANGE');
      mappedHeader.set('navChangePercent', 'pds.DAILY_NAV_CHANGE_PERCENTAGE');
    } else if (this.isSiteIntl) {
      mappedHeader.set('asOfDate', 'products.date');
      mappedHeader.set('nav', 'pds.NAV_VALUE');
      mappedHeader.set('navChangeValue', 'pds.DAILY_NAV_CHANGE');
      mappedHeader.set('navChangePercent', 'pds.DAILY_NAV_CHANGE_PERCENTAGE');
      if (
        this.siteConfigService.isCanada() &&
        this.state.pricingConfig.showMarketPriceData
      ) {
        mappedHeader.set('marketPriceValue', 'pds.MARKET_VALUE');
        mappedHeader.set('marketChangeValue', 'products.Market-Price-Change');
        mappedHeader.set(
          'marketChangePercent',
          'products.Market-Price-Change_PERCENTAGE'
        );
        mappedHeader.set('bidOrAskSpread', 'products.Bid-Ask-Spread');
        mappedHeader.set(
          'discountOrPremiumInception',
          'products.Avg-of-Market-Price-vs-NAV-since-Inception'
        );
        mappedHeader.set(
          'discountOrPremiumAtLastClose',
          'products.Avg-of-Market-Price-vs-NAV-at-Close'
        );
      }
    } else {
      mappedHeader.set('asOfDate', 'products.date');
      mappedHeader.set('applicationPrice', 'pds.APPLICATION_PRICE');
      mappedHeader.set('redemptionPrice', 'pds.REDEMPTION_PRICE');
      mappedHeader.set('nav', 'pds.NAV_VALUE');
      mappedHeader.set('applicationPrice', 'products.POP_VALUE');

      mappedHeader.set('navChangeValue', 'pds.DAILY_NAV_CHANGE');
      mappedHeader.set('navChangePercent', 'pds.DAILY_NAV_CHANGE_PERCENTAGE');

      mappedHeader.set(
        'masterFundNav',
        'products.Master-Fund-Level-Market-Nav'
      );
      mappedHeader.set(
        'dailyLiquidAssetRatio',
        'products.Liquid-Asset-Daily_PERCENTAGE'
      );
      mappedHeader.set(
        'weeklyLiquidAssetRatio',
        'products.Liquid-Asset-Weekly_PERCENTAGE'
      );

      mappedHeader.set('marketPriceValue', 'pds.MARKET_VALUE');
      mappedHeader.set('marketChangeValue', 'products.Market-Price-Change');
      mappedHeader.set(
        'marketChangePercent',
        'products.Market-Price-Change_PERCENTAGE'
      );
      mappedHeader.set('bidOrAskSpread', 'products.Bid-Ask-Spread');
      mappedHeader.set(
        'discountOrPremiumInception',
        'products.Avg-of-Market-Price-vs-NAV-since-Inception'
      );
      mappedHeader.set(
        'discountOrPremiumAtLastClose',
        'products.Avg-of-Market-Price-vs-NAV-at-Close'
      );
      mappedHeader.set('netShareHolderFlow', 'products.Shareholder-Net-Flow');
      mappedHeader.set(
        'masterNetShareHolderFlow',
        'products.Master-Shareholder-Net-Flow'
      );
    }

    const mappedFullRecords = [
      Object.keys(mappedBody[0]).map((data) =>
        this.translateService.instant(mappedHeader.get(data))
      ),
    ];

    mappedBody.forEach((data) => {
      mappedFullRecords.push(Object.values(data));
    });

    const fundName = [];
    const shareClassSection = [];
    const pricingSection = [];

    fundName.push([this.state.overviewData.fundName]);

    shareClassSection.push([
      `${this.translateService.instant('pds.SHARE_CLASS_NAME_STD')}`,
      `${this.state.overviewData.shareClass.shareClassName}`,
    ]);
    shareClassSection.push([
      `${this.translateService.instant('products.inception-date')}`,
      `${this.state.pricingConfig.dateValues.incDate}`,
    ]);
    shareClassSection.push([
      `${this.translateService.instant(
        'pds.NAV_VALUE'
      )} ${this.translateService.instant('products.as-of')}` +
        `${this.state.overviewData.nav.navAsOfDate}`,
      `${this.state.overviewData.nav.navValue}`,
    ]);

    pricingSection.push([
      `${this.translateService.instant(
        'products.price-historical-pricing-results'
      )}:${this.translateService.instant('products.calendar-from')}` +
        `${this.state.tableConfig.customRange.fromDate} ` +
        `${this.translateService.instant('products.calendar-to')}` +
        `${this.state.tableConfig.customRange.toDate}`,
    ]);

    pricingSection.push([
      `${this.translateService.instant(
        'pds.HIGH_NAV_VALUE_YTD'
      )}  ${this.translateService.instant(
        'products.price-excel-within-selected-range'
      )}:` +
        ` ${this.state.tableConfig.highestNav} ` +
        `${this.translateService.instant('products.price-history-on')}` +
        `${this.state.tableConfig.highestNavDate}`,
    ]);

    pricingSection.push([
      `${this.translateService.instant(
        'pds.LOW_NAV_VALUE_YTD'
      )} ${this.translateService.instant(
        'products.price-excel-within-selected-range'
      )}:` +
        ` ${this.state.tableConfig.lowestNav} ` +
        `${this.translateService.instant('products.price-history-on')}` +
        `${this.state.tableConfig.lowestNavDate}`,
    ]);

    forkJoin([
      this.getProximalCaveat('HistoricalPriceDataExcelBottom'),
      this.getProximalCaveat('HistoricalPriceDataExcelTop'),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [string[][], string[][]] = data;
      this.exportToExcel(
        mappedFullRecords,
        topCaveat,
        bottomCaveat,
        fundName,
        shareClassSection,
        pricingSection
      );
    });
  }

  /**
   * get the proximal caveats for a particular placement
   */
  getProximalCaveat(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.map((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  /**
   * Return mapped data as per downloadable action.
   * @param totalRecords total filtered records
   */
  mapDownloadData(
    totalRecords: PricingHistory[]
  ): PricingDownloadableTableRowData[] {
    const mappedData: PricingDownloadableTableRowData[] = [];
    if (this.state.pricingConfig.showApplicationPrice) {
      totalRecords.forEach((record) => {
        mappedData.push({
          asOfDate: record.asOfDate,
          applicationPrice: record.applicationPrice,
          redemptionPrice: record.redemptionPrice,
          nav: record.nav,
          navChangeValue: record.navChangeValue,
          navChangePercent: record.navChangePercent,
        });
      });
    } else if (this.siteConfigService.isCanada()) {
      if (this.state.pricingConfig.showMarketPriceData) {
        totalRecords.forEach((record) => {
          mappedData.push({
            asOfDate: record.asOfDate,
            nav: record.nav,
            navChangeValue: record.navChangeValue,
            navChangePercent: record.navChangePercent,
            marketPriceValue: record.marketPriceValue,
            marketChangeValue: record.marketChangeValue,
            marketChangePercent: record.marketChangePercent,
            bidOrAskSpread: record.bidOrAskSpread,
            discountOrPremiumInception: record.discountOrPremiumInception,
            discountOrPremiumAtLastClose: record.discountOrPremiumAtLastClose,
          });
        });
      } else {
        totalRecords.forEach((record) => {
          mappedData.push({
            asOfDate: record.asOfDate,
            nav: record.nav,
            navChangeValue: record.navChangeValue,
            navChangePercent: record.navChangePercent,
          });
        });
      }
    } else if (this.isSiteIntl) {
      totalRecords.forEach((record) => {
        mappedData.push({
          asOfDate: record.asOfDate,
          nav: record.nav,
          navChangeValue: record.navChangeValue,
          navChangePercent: record.navChangePercent,
        });
      });
    } else {
      totalRecords.forEach((record) => {
        let prcngHstryDt: PricingDownloadableTableRowData = {
          asOfDate: record.asOfDate,
        };

        if (this.state.pricingConfig.showApplicationPrice) {
          prcngHstryDt = {
            ...prcngHstryDt,
            applicationPrice: record.applicationPrice,
          };
        }

        if (this.state.pricingConfig.showRedemptionPrice) {
          prcngHstryDt = {
            ...prcngHstryDt,
            redemptionPrice: record.redemptionPrice,
          };
        }

        prcngHstryDt = { ...prcngHstryDt, nav: record.nav };

        if (this.state.pricingConfig.showPop) {
          prcngHstryDt = {
            ...prcngHstryDt,
            applicationPrice: record.applicationPrice,
          };
        }
        prcngHstryDt = {
          ...prcngHstryDt,
          navChangeValue: record.navChangeValue,
          navChangePercent: record.navChangePercent,
        };

        if (this.state.pricingConfig.showMasterFundNav) {
          prcngHstryDt = {
            ...prcngHstryDt,
            masterFundNav: record.masterFundNav,
          };
        }
        if (this.state.pricingConfig.showDailyLiquidAssetRatio) {
          prcngHstryDt = {
            ...prcngHstryDt,
            dailyLiquidAssetRatio: record.dailyLiquidAssetRatio,
          };
        }
        if (this.state.pricingConfig.showWeeklyLiquidAssetRatio) {
          prcngHstryDt = {
            ...prcngHstryDt,
            weeklyLiquidAssetRatio: record.weeklyLiquidAssetRatio,
          };
        }
        if (this.state.pricingConfig.showNetShareHolderFlow) {
          prcngHstryDt = {
            ...prcngHstryDt,
            netShareHolderFlow: record.netShareHolderFlow,
          };
        }

        if (this.state.pricingConfig.showMoneyMarketPriceData) {
          prcngHstryDt = {
            ...prcngHstryDt,
            masterFundNav: record.masterFundNav,
            dailyLiquidAssetRatio: record.dailyLiquidAssetRatio,
            weeklyLiquidAssetRatio: record.weeklyLiquidAssetRatio,
          };
        }

        if (this.state.pricingConfig.showMarketPriceData) {
          prcngHstryDt = {
            ...prcngHstryDt,
            marketPriceValue: record.marketPriceValue,
            marketChangeValue: record.marketChangeValue,
            marketChangePercent: record.marketChangePercent,
          };
        }

        if (
          this.state.pricingConfig.showMarketPriceData &&
          this.state.pricingConfig.showForETF
        ) {
          prcngHstryDt = {
            ...prcngHstryDt,
            bidOrAskSpread: record.bidOrAskSpread,
            discountOrPremiumInception: record.discountOrPremiumInception,
          };
        }
        if (this.state.pricingConfig.showMarketPriceData) {
          prcngHstryDt = {
            ...prcngHstryDt,
            discountOrPremiumAtLastClose: record.discountOrPremiumAtLastClose,
          };
        }
        if (this.state.pricingConfig.showMoneyMarketNetFlowColumn) {
          prcngHstryDt = {
            ...prcngHstryDt,
            netShareHolderFlow: record.netShareHolderFlow,
          };
        }
        if (this.state.pricingConfig.showMoneyMarketMasterNetFlowColumn) {
          prcngHstryDt = {
            ...prcngHstryDt,
            masterNetShareHolderFlow: record.masterNetShareHolderFlow,
          };
        }
        mappedData.push(prcngHstryDt);
      });
    }

    return mappedData;
  }

  /**
   * Export the table data to excel and auto download.
   * @param data full record for excel
   */
  exportToExcel(
    data: string[][],
    topCaveat: string[][],
    bottomCaveat: string[][],
    fundName: string[][],
    shareClassSection: string[][],
    pricingSection: string[][]
  ): void {
    import('xlsx').then((xlsx) => {
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();

      workSheet['!cols'] = this.fitToColumn(fundName);

      xlsx.utils.sheet_add_aoa(workSheet, fundName, {
        origin: 'A' + (topCaveat.length + 2),
      });

      xlsx.utils.sheet_add_aoa(workSheet, shareClassSection, {
        origin: 'A' + (topCaveat.length + fundName.length + 3),
      });

      xlsx.utils.sheet_add_aoa(workSheet, pricingSection, {
        origin:
          'A' +
          (topCaveat.length + fundName.length + shareClassSection.length + 4),
      });

      xlsx.utils.sheet_add_aoa(workSheet, data, {
        origin:
          'A' +
          (topCaveat.length +
            fundName.length +
            shareClassSection.length +
            pricingSection.length +
            6),
      });

      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin:
          'A' +
          (data.length +
            8 +
            topCaveat.length +
            fundName.length +
            shareClassSection.length +
            pricingSection.length),
      });
      const priceReportTabname = this.translateService.instant(
        'products.price-history-report-tabname'
      );
      const priceReportFilename = this.translateService.instant(
        'products.price-history-report-filename'
      );
      xlsx.utils.book_append_sheet(workBook, workSheet, priceReportTabname);
      xlsx.writeFile(workBook, priceReportFilename);
    });
  }
  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  fitToColumn(data: string[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(...data.map((a2) => a2[i].toString().length)),
    }));
  }

  showSharePrices(): boolean {
    // Don't show additional Share Prices for Canadian to match US version
    return (
      this.isSiteIntl &&
      !this.isTemit &&
      !this.isMartinCurrieGpt &&
      !this.siteConfigService.isCanada()
    );
  }

  showPricingBreakpoints(): boolean {
    return (
      !this.isSiteIntl ||
      this.isTemit ||
      this.isMartinCurrieGpt ||
      this.siteConfigService.isCanada()
    );
  }

  checkIfNavHighLowDisabledCountrySpecific(): boolean {
    return HIGHEST_LOWEST_NAV_DISABLED_COUNTRIES.has(
      this.siteConfigService.getPdsCountry()
    );
  }

  showNavHighLow(pricingState: PricingState): boolean {
    return (
      this.isSiteIntl &&
      !this.checkIfNavHighLowDisabledCountrySpecific() &&
      pricingState?.highLowNavTableData.length > 0
    );
  }

  showPremiumDiscountData(premiumDisData: InfoItem[]): boolean {
    return premiumDisData && premiumDisData?.length > 0;
  }

  showSalesChargeBreakPointsData(
    salesChargeBreakPointsData: SalesChargeBreakPoint[]
  ): boolean {
    return salesChargeBreakPointsData && salesChargeBreakPointsData?.length > 0;
  }

  showWeekRange(pricingConfig: PricingConfig): boolean {
    if (this.siteConfigService.isCanada()) {
      return (
        pricingConfig.productType === 'Mutual Funds' ||
        pricingConfig.productType === 'ETFs'
      );
    } else {
      return (
        !this.siteConfigService.isTemit() &&
        !this.siteConfigService.isMartinCurrieGpt()
      );
    }
  }
  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page records as observable.
   */
  getCurrentPage$(): Observable<PricingHistory[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }

  /**
   * Return total filtered records.
   */
  getTotalFilteredRecords(): PricingHistory[] {
    return this.pagedRecords.records;
  }

  public createPremiumDiscountNavViivChart(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): void {
    if (!hasValues(this.state.historicalData, 'viiv')) {
      // no Premium Discount viiv data, so return null
      this.state.premiumDiscountNavViivHistory = null;
      return;
    }
    logger.debug('createPremiumDiscountNavViivChart() HAS VALUES!');
    const {
      filteredPricingData,
      fromDateStd,
      toDateStd,
    } = this.getFilteredPricingDataByDateRange(
      this.state.historicalData,
      selectedToggleRange,
      fromDate,
      toDate
    );

    const chartConfig = {
      chartTitle: this.translateService.instant(
        'products.premium-discount-nav-viiv-historical-header'
      ),
      proximalPlacementTop: 'PremiumDiscountNavViivHistoryChartProximalTop',
      proximalPlacementBottom:
        'PremiumDiscountNavViivHistoryChartProximalBottom',
      caveatPlacement: 'PremiumDiscountNavViivHistoryChart',
      tooltip: this.translateService.tooltip(
        'products.premium-discount-nav-viiv-historical-header'
      ),
      selectedToggleRange,
      dateRangeEnum: dateRangeToggleInitialValues,
      customRange: {
        fromDateStd,
        toDateStd,
      },
      highChartOptions: this.fetchChartOptions('PREMIUM_DISCOUNT_NAV_VIIV'),
    };

    const navChartLabels = this.getNavChartLabels();
    chartConfig.highChartOptions.series = this.fetchPremiumDiscountNavViivSeries(
      filteredPricingData,
      this.state.overviewData.nav.currencyCode,
      navChartLabels
    );
    logger.debug('Premium Discount Nav Viiv chartConfig', chartConfig);

    this.state.premiumDiscountNavViivHistory = chartConfig;

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this._pricingState.next(this.state);
    }
  }

  private fetchPremiumDiscountNavViivSeries(
    navHistory: PricingHistory[],
    currencyCode: string,
    navChartLabels: NavChartLabels
  ): SeriesOptionsType[] {
    const navValues = this.convertNavHistoryIntoSeries(
      navHistory,
      CalcType.NAV
    );
    const currencySymbol = this.getCurrencySymbol(currencyCode);

    const vivValues = this.convertNavHistoryIntoSeries(
      navHistory,
      CalcType.PREMIUM_DISCOUNT_NAV_VIIV
    );

    const navSeries: CustomSeriesOptions = {
      name: navChartLabels.navLabel,
      type: 'line',
      min: 0,
      tooltip: {
        pointFormatter: this.pointFormatterProvider(
          true,
          navChartLabels.navChangePercentLabel,
          true,
          this.state.pricingConfig.showMoneyMarketPriceData
        ),
      },
      data: navValues,
      currencySymbol,
    };

    const vivSeries: CustomSeriesOptions = {
      name: navChartLabels.viivLabel,
      type: 'line',
      min: 0,
      tooltip: {
        pointFormatter: this.pointFormatterProvider(true, null, true),
      },
      data: vivValues,
      currencySymbol,
    };

    return [navSeries, vivSeries];
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
